import { useEffect, useState } from 'react';

/**
 * Mutabakat cevap sayfası — KARŞI TARAFIN gördüğü tek ekran.
 *
 * Bu sayfayı açan kişi bizim kullanıcımız değil ve olması da beklenemez:
 * tedarikçiye hesap açmak, ona ekstre göndermekten çok daha büyük bir taahhüt.
 * Kimlik yerine e-postadaki belirteç geçiyor.
 *
 * UYGULAMANIN GERİ KALANINDAN AYRI DURUYOR — kasıt bu. Ne AppShell, ne menü, ne
 * oturum. `App.tsx` bu yolu oturum kapılarından ÖNCE ele alıyor, çünkü aksi
 * hâlde anonim ziyaretçi giriş ekranına düşerdi: e-postadaki bağlantı, alıcıyı
 * parola isteyen bir forma götüren bir bağlantı olurdu ve kimse bir daha
 * tıklamazdı.
 *
 * Sayfa hiçbir şey ezberlemiyor ve hiçbir şey saklamıyor: belirteç yalnızca
 * adres çubuğunda, istek başlığında değil; localStorage'a hiçbir şey yazılmıyor.
 */

interface Ozet {
  id: string;
  companyName: string;
  payeeName: string;
  periodStart: string;
  periodEnd: string;
  balance: number;
  currency: string;
  status: string;
  respondedAt: string | null;
}

const trDate = (iso: string) =>
  new Intl.DateTimeFormat('tr-TR', { dateStyle: 'long' }).format(new Date(iso));

const trAmount = (v: number) =>
  new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);

/** Bakiyeyi kelimeyle söyler; okuyan kişi bizim defter mantığımızı bilmiyor. */
function bakiyeCumlesi(balance: number, currency: string): string {
  if (balance === 0) return 'Bakiye görünmüyor.';
  return balance < 0
    ? `${trAmount(Math.abs(balance))} ${currency} tutarında ALACAKLI görünüyorsunuz.`
    : `${trAmount(Math.abs(balance))} ${currency} tutarında BORÇLU görünüyorsunuz.`;
}

export function ReconciliationPublicPage() {
  const [ozet, setOzet] = useState<Ozet | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [not, setNot] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [sonuc, setSonuc] = useState<'AGREED' | 'DISPUTED' | null>(null);

  const parts = window.location.pathname.split('/');
  const id = parts[parts.length - 1] ?? '';
  const token = new URLSearchParams(window.location.search).get('t') ?? '';

  useEffect(() => {
    let iptal = false;
    (async () => {
      try {
        const r = await fetch(
          `/api/v1/payees/reconciliations/${encodeURIComponent(id)}/public?t=${encodeURIComponent(token)}`,
        );
        const j = await r.json().catch(() => null);
        if (iptal) return;
        if (!r.ok) {
          setHata(j?.error?.message ?? 'Bağlantı geçersiz ya da süresi dolmuş.');
          return;
        }
        setOzet(j as Ozet);
        if (j?.status === 'AGREED' || j?.status === 'DISPUTED') setSonuc(j.status);
      } catch {
        if (!iptal) setHata('Bağlantıya ulaşılamadı. Lütfen sonra tekrar deneyin.');
      }
    })();
    return () => {
      iptal = true;
    };
  }, [id, token]);

  const cevapla = async (agreed: boolean) => {
    setGonderiliyor(true);
    setHata(null);
    try {
      const r = await fetch(`/api/v1/payees/reconciliations/${encodeURIComponent(id)}/respond`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ t: token, agreed, note: not.trim() || undefined }),
      });
      const j = await r.json().catch(() => null);
      if (!r.ok) {
        setHata(j?.error?.message ?? 'Cevap kaydedilemedi.');
        return;
      }
      setSonuc(agreed ? 'AGREED' : 'DISPUTED');
    } catch {
      setHata('Cevap gönderilemedi. Lütfen sonra tekrar deneyin.');
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <div className="bg-bg text-fg flex min-h-dvh items-start justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <h1 className="text-fg text-xl font-semibold">Cari Hesap Mutabakatı</h1>

        {hata && !ozet && (
          <div className="border-border mt-6 rounded-xl border p-5">
            <p className="text-danger text-sm">{hata}</p>
            <p className="text-fg-muted mt-3 text-sm">
              Bağlantının süresi dolmuş olabilir ya da bu mutabakat için zaten cevap verilmiş
              olabilir. Gönderen firmayla iletişime geçebilirsiniz.
            </p>
          </div>
        )}

        {ozet && (
          <>
            <p className="text-fg-muted mt-1 text-sm">{ozet.companyName} tarafından gönderildi</p>

            <div className="border-border mt-6 rounded-xl border p-5">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-muted">Firma</dt>
                  <dd className="text-fg text-right font-medium">{ozet.payeeName}</dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt className="text-fg-muted">Dönem</dt>
                  <dd className="text-fg text-right">
                    {trDate(ozet.periodStart)} — {trDate(ozet.periodEnd)}
                  </dd>
                </div>
              </dl>
              <p className="text-fg mt-4 text-base">
                {trDate(ozet.periodEnd)} tarihi itibarıyla {ozet.companyName} kayıtlarında{' '}
                <strong>{bakiyeCumlesi(ozet.balance, ozet.currency)}</strong>
              </p>
              <p className="text-fg-muted mt-2 text-xs">
                Ayrıntılı ekstre, bu bağlantıyı aldığınız e-postanın ekindedir.
              </p>
            </div>

            {sonuc ? (
              <div className="border-border mt-6 rounded-xl border p-5">
                <p className="text-fg text-sm">
                  {sonuc === 'AGREED'
                    ? 'Mutabık olduğunuzu bildirdiniz. Teşekkür ederiz.'
                    : 'İtirazınız kaydedildi. Gönderen firma sizinle iletişime geçecek.'}
                </p>
              </div>
            ) : (
              <div className="border-border mt-6 rounded-xl border p-5">
                <p className="text-fg text-sm font-medium">Bu bakiyeye mutabık mısınız?</p>
                {hata && <p className="text-danger mt-2 text-sm">{hata}</p>}

                <label htmlFor="mut-not" className="text-fg-muted mt-4 block text-xs">
                  İtirazınız varsa açıklayın (isteğe bağlı)
                </label>
                <textarea
                  id="mut-not"
                  rows={3}
                  maxLength={2000}
                  value={not}
                  onChange={(e) => setNot(e.target.value)}
                  className="border-border bg-bg text-fg focus:border-primary mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none"
                  placeholder="Örn: 12.03.2026 tarihli 5.000 TL ödememiz görünmüyor."
                />

                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={gonderiliyor}
                    onClick={() => void cevapla(true)}
                    className="bg-primary min-h-11 rounded-lg px-4 text-sm font-medium text-white disabled:opacity-60"
                  >
                    Mutabıkım
                  </button>
                  <button
                    type="button"
                    disabled={gonderiliyor}
                    onClick={() => void cevapla(false)}
                    className="border-border text-fg min-h-11 rounded-lg border px-4 text-sm font-medium disabled:opacity-60"
                  >
                    İtirazım var
                  </button>
                </div>
                <p className="text-fg-muted mt-3 text-xs">
                  Cevabınızı bir kez verebilirsiniz; bağlantı sonrasında kapanır.
                </p>
              </div>
            )}
          </>
        )}

        {!ozet && !hata && <p className="text-fg-muted mt-6 text-sm">Yükleniyor…</p>}
      </div>
    </div>
  );
}
